import { useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Plus, Trash2, Pencil } from 'lucide-react';
import useRolePermissions from '@/hooks/admin/shared/useRolePermissions';
import BusinessTransactionsSubNav from './BusinessTransactionsSubNav';
import BusinessTransactionsIncomeView, { 
  BusinessTransactionsIncomeViewRef 
} from './BusinessTransactionsIncomeView';
import BusinessTransactionsExpensesView, { 
  BusinessTransactionsExpensesViewRef 
} from './BusinessTransactionsExpensesView';
import { formatCurrency } from '@/utils/shared/currencyUtils';

type SubTab = 'income' | 'expenses';

interface SelectionState {
  count: number;
  total: number;
  onDelete: () => void;
  onEdit?: () => void;
  isDeleting: boolean;
}

const BusinessTransactionsTab = () => {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('income');
  const [incomeSelection, setIncomeSelection] = useState<SelectionState | null>(null);
  const [expensesSelection, setExpensesSelection] = useState<SelectionState | null>(null);
  
  const { canCreate, canDelete, canEdit, canDownload } = useRolePermissions();

  const incomeViewRef = useRef<BusinessTransactionsIncomeViewRef>(null);
  const expensesViewRef = useRef<BusinessTransactionsExpensesViewRef>(null);

  const handleDownloadCSV = () => {
    if (activeSubTab === 'income') {
      incomeViewRef.current?.downloadCSV();
    } else {
      expensesViewRef.current?.downloadCSV();
    }
  };

  const handleCreate = () => {
    if (activeSubTab === 'income') {
      incomeViewRef.current?.createNew();
    } else {
      expensesViewRef.current?.createNew();
    }
  };

  const handleIncomeSelectionChange = useCallback((selection: SelectionState | null) => {
    setIncomeSelection(selection);
  }, []);

  const handleExpensesSelectionChange = useCallback((selection: SelectionState | null) => {
    setExpensesSelection(selection);
  }, []);

  const currentSelection = activeSubTab === 'income' ? incomeSelection : expensesSelection;
  const hasSelection = currentSelection && currentSelection.count > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between min-h-10">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold">Transacciones del negocio</h2>
            
            {hasSelection && (
              <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                {currentSelection.count} seleccionado{currentSelection.count > 1 ? 's' : ''} • {formatCurrency(currentSelection.total)}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {hasSelection && canDelete && (
              <Button
                variant="destructive"
                onClick={currentSelection.onDelete}
                className="flex items-center gap-2"
                disabled={currentSelection.isDeleting}
              >
                <Trash2 size={16} />
                Eliminar
              </Button>
            )}

            {hasSelection && canEdit && currentSelection.onEdit && (
              <Button
                variant="outline"
                onClick={currentSelection.onEdit}
                className="flex items-center gap-2"
              >
                <Pencil size={16} />
                Editar
              </Button>
            )}

            {canDownload && (
              <Button
                onClick={handleDownloadCSV}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Download size={16} />
                Descargar CSV
              </Button>
            )}

            {canCreate && (
              <Button
                onClick={handleCreate}
                className="bg-brand-primary hover:bg-brand-primary-hover flex items-center gap-2"
              >
                <Plus size={16} />
                {activeSubTab === 'income' ? 'Nuevo Ingreso' : 'Nuevo Egreso'}
              </Button>
            )}
          </div>
        </div>

        <BusinessTransactionsSubNav
          activeSubTab={activeSubTab}
          onSubTabChange={setActiveSubTab}
        />
      </div>

      {/* Content */}
      {activeSubTab === 'income' ? (
        <BusinessTransactionsIncomeView 
          ref={incomeViewRef}
          onSelectionChange={handleIncomeSelectionChange}
        />
      ) : (
        <BusinessTransactionsExpensesView 
          ref={expensesViewRef}
          onSelectionChange={handleExpensesSelectionChange}
        />
      )}
    </div>
  );
};

export default BusinessTransactionsTab;